import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { HOME } from "@/constants/testIds";
import {
  Building2,
  Users,
  Sparkles,
  Quote,
  Landmark,
  Banknote,
  Mail,
  Phone,
} from "lucide-react";

const pillars = [
  {
    icon: Building2,
    title: "Building a House of God",
    body: "A sacred space dedicated to worship, sacrament, and prayer for generations to come.",
  },
  {
    icon: Users,
    title: "Building Faith & Community",
    body: "Stronger GKKs, deeper ministries, and a parish where every family has a place.",
  },
  {
    icon: Sparkles,
    title: "Building Hope & Peace",
    body: "A beacon of the Franciscan witness — simplicity, compassion, and unwavering trust in God.",
  },
];

const benefactors = [
  { level: "Platinum", amount: "₱1,000,000.00", accent: "from-gold to-gold/60" },
  { level: "Gold", amount: "₱500,000.00", accent: "from-gold/90 to-gold/40" },
  { level: "Silver", amount: "₱200,000.00", accent: "from-parchment-300 to-parchment-200" },
  { level: "Bronze", amount: "₱100,000.00", accent: "from-terracotta to-terracotta-dark" },
];

const budget = [
  ["Permits and Clearances", "96,140.00"],
  ["Project Billboard / Signboard", "20,758.66"],
  ["Occupational Safety and Health", "198,472.18"],
  ["Mobilization / Demobilization", "120,162.35"],
  ["Additional Geotechnical Investigation", "258,060.00"],
  ["Excavation Works", "123,053.32"],
  ["Gravel Fill Works", "359,837.89"],
  ["Concrete Works", "2,529,819.49"],
  ["Reinforcing Steel Works", "2,033,980.32"],
  ["Formworks and Falseworks", "628,212.24"],
  ["Soil Poisoning", "108,141.30"],
  ["Masonry Works", "358,559.97"],
  ["Fabricated Metal Roofing Accessory (G26, Ridge/Hip Rolls)", "74,683.47"],
  ["Fabricated Metal Roofing Accessory (G26, Flashing)", "51,080.96"],
  ["Pre-painted Metal Sheets, Rib Type, Long Span", "1,627,574.05"],
  ["Metal Structures", "13,714,485.96"],
  ["Steel Deck", "818,454.89"],
];

export default function Construction() {
  const [data, setData] = useState({
    percentage: 20,
    phase_label: "Phase 1 — Foundation",
    note: "Updated regularly by the parish office.",
  });

  useEffect(() => {
    api.get("/construction").then((res) => setData(res.data)).catch(() => {});
  }, []);

  return (
    <section id="construction" className="relative overflow-hidden">
      {/* ───────── HERO BLOCK: progress + donation card ───────── */}
      <div className="relative py-24 md:py-32 bg-cocoa-900">
        <div className="absolute inset-0 bg-gradient-to-br from-cocoa-900 via-cocoa-800 to-cocoa-900" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(212,175,55,0.18),transparent_55%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_90%,rgba(194,89,83,0.14),transparent_55%)]" />
        <div className="absolute inset-0 grain" />

        <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
          <div className="max-w-3xl">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-gold/70" />
              <span className="text-[11px] uppercase tracking-[0.3em] text-gold font-sans">
                Parish Project
              </span>
            </div>
            <div className="text-[10px] uppercase tracking-[0.32em] text-parchment-200/70 mb-3 font-sans">
              Journeying in Faith · Building Our Parish
            </div>
            <h2 className="font-serif text-parchment-100 text-[clamp(2rem,4vw,3.6rem)] leading-[1.05]">
              Commemorating the
              <span className="block italic text-gold/95">800th Anniversary</span>
              <span className="block text-parchment-100/90 text-2xl md:text-3xl mt-3 font-normal">
                of the Transit of Saint Francis of Assisi · 1226 — 2026
              </span>
            </h2>
            <p className="mt-6 font-sans text-parchment-200/85 font-light text-lg leading-relaxed">
              We continue to build not only a church structure, but a spiritual home
              for families, children, youth, and communities. Every contribution
              becomes part of a sacred offering.
            </p>
          </div>

          <div className="mt-16 grid lg:grid-cols-12 gap-8">
            {/* Progress card */}
            <div className="lg:col-span-7 bg-parchment-100/[0.04] backdrop-blur-sm border border-parchment-100/15 p-8 md:p-10">
              <div className="text-[10px] uppercase tracking-[0.28em] text-gold mb-4 font-sans">
                Project Progress
              </div>
              <div className="flex items-baseline gap-4 mb-2">
                <div className="font-serif text-parchment-100 text-6xl md:text-7xl tracking-tight">
                  {data.percentage}
                  <span className="text-3xl text-gold">%</span>
                </div>
                <div className="font-sans text-parchment-200/70 text-sm">
                  {data.phase_label}
                </div>
              </div>

              <div
                data-testid={HOME.constructionProgressBar}
                className="mt-6 h-1.5 bg-parchment-100/15 overflow-hidden"
                role="progressbar"
                aria-valuenow={data.percentage}
                aria-valuemin={0}
                aria-valuemax={100}
              >
                <div
                  className="h-full bg-gradient-to-r from-gold via-terracotta to-gold transition-all duration-1000"
                  style={{ width: `${Math.min(100, Math.max(0, data.percentage))}%` }}
                />
              </div>

              <p className="mt-6 font-sans text-parchment-200/70 text-sm italic">
                {data.note}
              </p>

              <div className="mt-10 grid grid-cols-3 gap-6 border-t border-parchment-100/15 pt-8">
                {[
                  { l: "Foundation", v: "Phase 1" },
                  { l: "Structure", v: "Phase 2" },
                  { l: "Finishing", v: "Phase 3" },
                ].map((p) => (
                  <div key={p.l}>
                    <div className="font-serif italic text-gold text-lg">{p.v}</div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-parchment-200/60 mt-1 font-sans">
                      {p.l}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Donation summary card */}
            <div className="lg:col-span-5 bg-parchment-100 text-cocoa-900 p-8 md:p-10">
              <div className="text-[10px] uppercase tracking-[0.28em] text-sage-dark mb-3 font-sans">
                Support the Project
              </div>
              <h3 className="font-serif text-3xl text-cocoa-900 leading-tight">
                Become part of a
                <span className="italic block text-terracotta">sacred offering.</span>
              </h3>

              <div className="mt-6 space-y-3 font-sans text-cocoa-800 text-sm leading-relaxed">
                <div className="flex justify-between border-b border-parchment-400 pb-3">
                  <span className="text-cocoa-700">Donation Name</span>
                  <span className="font-medium text-right">St. Francis Parish</span>
                </div>
                <div className="flex justify-between border-b border-parchment-400 pb-3">
                  <span className="text-cocoa-700">Purpose</span>
                  <span className="font-medium text-right">Church Construction</span>
                </div>
                <div className="flex justify-between border-b border-parchment-400 pb-3">
                  <span className="text-cocoa-700">Contact</span>
                  <a href="tel:+639155811419" className="font-medium text-right hover:text-terracotta">
                    +63 915 581 1419
                  </a>
                </div>
                <div className="flex justify-between">
                  <span className="text-cocoa-700">Email</span>
                  <a href="mailto:sfap2028@gmail.com" className="font-medium text-right hover:text-terracotta">
                    sfap2028@gmail.com
                  </a>
                </div>
              </div>

              <a
                href="#bank-transfer"
                data-testid={HOME.constructionContactCta}
                className="mt-8 inline-flex items-center justify-center w-full gap-3 bg-cocoa-900 text-parchment-100 px-6 py-4 font-sans text-sm tracking-wide hover:bg-terracotta transition-colors group"
              >
                See How to Donate
                <span className="transition-transform group-hover:translate-x-1">↓</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ───────── LETTER FROM THE PARISH PRIEST ───────── */}
      <div className="bg-parchment-100 py-24 md:py-32">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="flex items-center gap-3 mb-6 justify-center">
            <span className="h-px w-10 bg-sage" />
            <span className="text-[11px] uppercase tracking-[0.3em] text-sage-dark font-sans">
              A Letter from the Parish Priest
            </span>
            <span className="h-px w-10 bg-sage" />
          </div>

          <h3 className="font-serif text-4xl md:text-5xl text-cocoa-900 text-center leading-tight">
            <span className="italic text-terracotta">Greetings of peace</span> and blessings.
          </h3>

          <div className="mt-12 grid md:grid-cols-12 gap-10 items-start">
            <div className="md:col-span-1 hidden md:flex justify-center">
              <Quote className="text-terracotta/70" size={40} strokeWidth={1.2} />
            </div>

            <div className="md:col-span-11 space-y-5 font-sans text-cocoa-800 leading-relaxed font-light text-[16px] md:text-[17px]">
              <p>
                As we joyfully commemorate the <em className="text-terracotta not-italic font-medium">800th Anniversary of the Transit of Saint Francis of Assisi</em>,
                let us be inspired by his example of simplicity, compassion, and unwavering trust in God.
                Today, we humbly reach out to you for support in building Saint Francis of Assisi Parish &mdash;
                a spiritual home where faith grows, lives are transformed, and generations are inspired.
              </p>

              <div className="border-l-2 border-terracotta pl-5 my-8">
                <p className="font-serif text-xl md:text-2xl text-cocoa-900 italic leading-snug">
                  Be a part of our legacy.
                </p>
                <p className="mt-2 text-cocoa-700">
                  Your generous contribution will help build a sacred space for prayer,
                  worship, and community service &mdash; a lasting testament of our faith and love for God.
                </p>
              </div>

              <p>
                <em className="font-medium not-italic text-cocoa-900">Your support today builds tomorrow&rsquo;s legacy.</em>{" "}
                Together, let us construct not only a building, but a living testimony of faith, hope, and love.
                Every gift counts &mdash; your generosity will help build not just walls, but living testimonies of our faith.
              </p>

              <p>
                Together, let us build more than a structure &mdash; let us build a beacon of faith, hope, and peace
                for many generations. We sincerely thank you for your kindness, generosity, and prayers.
                May our Lord bless you abundantly and reward you a hundredfold for your support.
              </p>

              <div className="mt-12 pt-8 border-t border-parchment-400">
                <p className="text-cocoa-700">Respectfully yours,</p>
                <p className="font-serif italic text-3xl text-terracotta mt-4">Fr. Ariel Doroteo Destora</p>
                <p className="text-[11px] uppercase tracking-[0.28em] text-cocoa-900 mt-2 font-sans">
                  Fr. Ariel Doroteo Destora
                </p>
                <p className="text-[11px] uppercase tracking-[0.22em] text-sage-dark mt-1 font-sans">
                  Parish Priest · Saint Francis of Assisi Parish
                </p>
              </div>
            </div>
          </div>

          {/* St. Francis quote */}
          <div className="mt-20 text-center max-w-2xl mx-auto">
            <Quote className="text-gold mx-auto mb-4" size={28} strokeWidth={1.2} />
            <p className="font-serif italic text-3xl md:text-4xl text-cocoa-900 leading-tight">
              &ldquo;Lord, make me an instrument of your peace.&rdquo;
            </p>
            <p className="mt-4 text-[11px] uppercase tracking-[0.28em] text-sage-dark font-sans">
              — Saint Francis of Assisi
            </p>
          </div>
        </div>
      </div>

      {/* ───────── THREE PILLARS ───────── */}
      <div className="bg-parchment-200 py-24 md:py-32 grain">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="text-[11px] uppercase tracking-[0.3em] text-sage-dark mb-3 font-sans">
              What we are building
            </div>
            <h3 className="font-serif text-4xl md:text-5xl text-cocoa-900 leading-tight">
              More than walls &mdash; <span className="italic text-terracotta">a living testimony.</span>
            </h3>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-parchment-400">
            {pillars.map(({ icon: Icon, title, body }) => (
              <div key={title} className="bg-parchment-100 p-10 md:p-12">
                <Icon className="text-terracotta mb-6" size={34} strokeWidth={1.2} />
                <div className="text-[10px] uppercase tracking-[0.28em] text-sage-dark mb-2 font-sans">
                  Building
                </div>
                <h4 className="font-serif text-2xl md:text-3xl text-cocoa-900 mb-4 leading-tight">
                  {title}
                </h4>
                <p className="font-sans text-cocoa-800 font-light leading-relaxed">
                  {body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ───────── WAYS TO GIVE — Benefactor levels ───────── */}
      <div className="bg-parchment-100 py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-16">
            <div className="flex items-center gap-3 mb-4 justify-center">
              <span className="h-px w-10 bg-sage" />
              <span className="text-[11px] uppercase tracking-[0.3em] text-sage-dark font-sans">
                Yes, I/we want to help build
              </span>
              <span className="h-px w-10 bg-sage" />
            </div>
            <h3 className="font-serif text-4xl md:text-5xl text-cocoa-900 leading-tight">
              Ways to Give
            </h3>
            <p className="mt-5 font-sans text-cocoa-800 max-w-2xl mx-auto font-light leading-relaxed">
              Choose a benefactor level below, or give any amount the Lord places on your heart.
              Every gift is a sacred offering.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {benefactors.map((b) => (
              <article
                key={b.level}
                className="group relative border border-parchment-400 bg-parchment-50 p-8 hover:-translate-y-1 transition-transform"
              >
                <div className={`h-1 w-12 bg-gradient-to-r ${b.accent} mb-6`} />
                <div className="text-[10px] uppercase tracking-[0.28em] text-sage-dark mb-2 font-sans">
                  Benefactor
                </div>
                <h4 className="font-serif text-2xl md:text-3xl text-cocoa-900">
                  {b.level}
                </h4>
                <div className="mt-6 font-serif text-2xl text-terracotta">
                  {b.amount}
                </div>
              </article>
            ))}
          </div>

          {/* Other amount card */}
          <div className="mt-5 bg-cocoa-900 text-parchment-100 p-8 md:p-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-gold mb-2 font-sans">
                Other Amount
              </div>
              <div className="font-serif text-2xl md:text-3xl">Give what the Lord places on your heart.</div>
              <p className="mt-2 font-sans text-parchment-200/80 text-sm">
                Any amount is welcomed and gratefully received.
                Official Parish receipts will be issued for every donation.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <a
                href="/donor-form"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="construction-pledge-form"
                className="inline-flex items-center gap-3 bg-parchment-100 text-cocoa-900 px-7 py-3.5 font-sans text-sm tracking-wide hover:bg-gold transition-colors"
              >
                Print Pledge Form
                <span>↗</span>
              </a>
              <a
                href="#bank-transfer"
                className="inline-flex items-center gap-3 bg-gold text-cocoa-900 px-7 py-3.5 font-sans text-sm tracking-wide hover:bg-parchment-100 transition-colors"
              >
                How to Donate
                <span>↓</span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ───────── PHASE 1 BUDGET BREAKDOWN ───────── */}
      <div className="bg-parchment-200 py-24 md:py-32 grain">
        <div className="max-w-5xl mx-auto px-6 lg:px-12">
          <div className="text-center mb-12">
            <div className="text-[11px] uppercase tracking-[0.3em] text-terracotta mb-3 font-sans">
              Transparency
            </div>
            <h3 className="font-serif text-4xl md:text-5xl text-cocoa-900 leading-tight">
              Phase 1 Budget Breakdown
            </h3>
            <p className="mt-5 font-sans text-cocoa-800 font-light text-sm max-w-xl mx-auto">
              A line-by-line look at the cost of building the foundation, frame, and roof of the new parish church.
            </p>
          </div>

          <div className="bg-parchment-50 border border-parchment-400">
            <ul>
              {budget.map(([item, peso], idx) => (
                <li
                  key={item}
                  className={`grid grid-cols-12 gap-4 px-6 md:px-8 py-4 items-baseline ${
                    idx < budget.length - 1 ? "border-b border-parchment-300" : ""
                  } ${idx % 2 === 1 ? "bg-parchment-100/40" : ""}`}
                >
                  <span className="col-span-1 font-serif text-terracotta">
                    {String(idx + 1).padStart(2, "0")}
                  </span>
                  <span className="col-span-8 font-sans text-cocoa-900 text-sm md:text-[15px] leading-snug">
                    {item}
                  </span>
                  <span className="col-span-3 text-right font-sans text-cocoa-800 text-sm md:text-[15px]">
                    ₱ {peso}
                  </span>
                </li>
              ))}
            </ul>

            <div className="bg-cocoa-900 text-parchment-100 px-6 md:px-8 py-6 flex flex-col md:flex-row items-baseline justify-between gap-2">
              <div className="text-[11px] uppercase tracking-[0.3em] text-gold font-sans">
                Total Contract Amount
              </div>
              <div className="font-serif text-3xl md:text-4xl text-gold">
                ₱ 23,176,277.05
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ───────── BANK TRANSFER / HOW TO DONATE ───────── */}
      <div id="bank-transfer" className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0 bg-cocoa-900" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_70%,rgba(212,175,55,0.12),transparent_60%)]" />

        <div className="relative max-w-5xl mx-auto px-6 lg:px-12 text-parchment-100">
          <div className="text-center mb-14">
            <div className="text-[11px] uppercase tracking-[0.3em] text-gold mb-3 font-sans">
              How to Donate
            </div>
            <h3 className="font-serif text-4xl md:text-5xl leading-tight">
              <span className="italic text-gold">1.</span> Bank Transfer
            </h3>
            <p className="mt-4 font-sans text-parchment-200/80 text-sm max-w-xl mx-auto font-light">
              You may also coordinate with the Parish Office for cash donations,
              check payments, and the issuance of your official receipt.
            </p>
          </div>

          <div className="grid md:grid-cols-12 gap-8">
            {/* Bank card */}
            <div className="md:col-span-7 bg-parchment-100/[0.05] border border-parchment-100/15 backdrop-blur-sm p-8 md:p-10">
              <div className="flex items-center gap-3 mb-6">
                <Landmark className="text-gold" size={22} strokeWidth={1.4} />
                <span className="text-[10px] uppercase tracking-[0.28em] text-gold font-sans">
                  Bank Details
                </span>
              </div>

              <dl className="space-y-5 font-sans">
                <Row label="Bank Name" value="BPI — General Santos Heights" />
                <Row label="Account Number" value="007303-0062-86" mono />
                <Row
                  label="Account Name"
                  value={
                    <>
                      The Roman Catholic Bishop of Marbel, Inc.
                      <br />
                      <span className="text-gold">St. Francis of Assisi Parish</span>
                    </>
                  }
                />
                <Row
                  label="Contact"
                  value={
                    <a href="tel:+639155811419" className="hover:text-gold">
                      +63 915 581 1419
                    </a>
                  }
                />
                <Row
                  label="Email"
                  value={
                    <a href="mailto:sfap2028@gmail.com" className="hover:text-gold">
                      sfap2028@gmail.com
                    </a>
                  }
                />
              </dl>
            </div>

            {/* Other ways */}
            <div className="md:col-span-5 space-y-6">
              <div className="bg-parchment-100 text-cocoa-900 p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Banknote className="text-terracotta" size={20} strokeWidth={1.4} />
                  <span className="text-[10px] uppercase tracking-[0.28em] text-sage-dark font-sans">
                    Cash or Check
                  </span>
                </div>
                <p className="font-serif text-xl mb-3">
                  Visit the Parish Office during office hours.
                </p>
                <p className="font-sans text-cocoa-700 text-sm leading-relaxed">
                  Please make checks payable to{" "}
                  <strong className="text-cocoa-900">St. Francis of Assisi Parish.</strong>
                </p>
                <p className="font-sans text-cocoa-700 text-sm mt-3 italic">
                  Collect your <span className="text-terracotta font-medium not-italic">Official Receipt</span> — thank you very much!
                </p>
              </div>

              <div className="bg-gold/10 border border-gold/30 p-8 text-parchment-100">
                <div className="flex items-center gap-3 mb-3">
                  <Phone size={18} className="text-gold" />
                  <span className="text-[10px] uppercase tracking-[0.28em] text-gold font-sans">
                    Parish Office
                  </span>
                </div>
                <a href="tel:+639155811419" className="font-serif text-2xl hover:text-gold">
                  0915 581 1419
                </a>
                <div className="mt-3 flex items-center gap-2 text-sm text-parchment-200/80">
                  <Mail size={14} />
                  <a href="mailto:sfap2028@gmail.com" className="hover:text-gold">
                    sfap2028@gmail.com
                  </a>
                </div>
              </div>
            </div>
          </div>

          <p className="mt-14 text-center font-sans text-parchment-200/70 italic max-w-2xl mx-auto">
            ✦ Thank you for your generosity. Every gift becomes a brick, a beam, a window of grace
            in this house of God.
          </p>
        </div>
      </div>
    </section>
  );
}

function Row({ label, value, mono }) {
  return (
    <div className="grid grid-cols-12 gap-4 border-b border-parchment-100/15 pb-4 last:border-0 last:pb-0">
      <dt className="col-span-12 md:col-span-4 text-[10px] uppercase tracking-[0.24em] text-parchment-200/70">
        {label}
      </dt>
      <dd
        className={`col-span-12 md:col-span-8 text-parchment-100 ${
          mono ? "font-mono text-lg tracking-wide" : "font-serif text-lg leading-snug"
        }`}
      >
        {value}
      </dd>
    </div>
  );
}
